import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '../components/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/card';
import { Progress } from '../components/progress';
import { Badge } from '../components/badge';
import { Brain, ArrowRight, ArrowLeft, RotateCcw, BarChart3, FileText, CheckCircle, AlertTriangle, XCircle, Minus, Lightbulb, ClipboardCheck, Users, Handshake, CloudLightning } from 'lucide-react';
import { toast } from 'react-toastify';
import Navbar from '../components/Navbar';
import Sidebar from '../components/Sidebar';
import api from "../sever/Api";
import { generateAIReport } from "../sever/Api";
import { notifyTestCompletion } from "../lib/testUtils";
// Quiz data structure
const quizData = {
  openness: {
    title: " Openness to Experience",
    description: "Curiosity, imagination, preference for novelty",
    questions: [
      {
        id: "o1",
        text: "I enjoy exploring new ideas and experiences.",
        options: [
          { value: "a", text: "Strongly Agree", score: 4, icon: "check" },
          { value: "b", text: "Agree", score: 3, icon: "triangle" },
          { value: "c", text: "Disagree", score: 2, icon: "diamond" },
          { value: "d", text: "Strongly Disagree", score: 1, icon: "x" },
        ],
      },
      {
        id: "o2",
        text: "I often think about abstract concepts.",
        options: [
          { value: "a", text: "Strongly Agree", score: 4, icon: "check" },
          { value: "b", text: "Agree", score: 3, icon: "triangle" },
          { value: "c", text: "Disagree", score: 2, icon: "diamond" },
          { value: "d", text: "Strongly Disagree", score: 1, icon: "x" },
        ],
      },
      {
        id: "o3",
        text: "I prefer variety over routine.",
        options: [
          { value: "a", text: "Strongly Agree", score: 4, icon: "check" },
          { value: "b", text: "Agree", score: 3, icon: "triangle" },
          { value: "c", text: "Disagree", score: 2, icon: "diamond" },
          { value: "d", text: "Strongly Disagree", score: 1, icon: "x" },
        ],
      },
      {
        id: "o4",
        text: "I enjoy creative hobbies like writing or painting.",
        options: [
          { value: "a", text: "Strongly Agree", score: 4, icon: "check" },
          { value: "b", text: "Agree", score: 3, icon: "triangle" },
          { value: "c", text: "Disagree", score: 2, icon: "diamond" },
          { value: "d", text: "Strongly Disagree", score: 1, icon: "x" },
        ],
      },
      {
        id: "o5",
        text: "I seek out intellectual discussions.",
        options: [
          { value: "a", text: "Strongly Agree", score: 4, icon: "check" },
          { value: "b", text: "Agree", score: 3, icon: "triangle" },
          { value: "c", text: "Disagree", score: 2, icon: "diamond" },
          { value: "d", text: "Strongly Disagree", score: 1, icon: "x" },
        ],
      },
    ],
  },
  conscientiousness: {
    title: " Conscientiousness",
    description: "Self-discipline, reliability, attention to detail",
    questions: [
      {
        id: "c1",
        text: "I like to follow a schedule.",
        options: [
          { value: "a", text: "Strongly Agree", score: 4, icon: "check" },
          { value: "b", text: "Agree", score: 3, icon: "triangle" },
          { value: "c", text: "Disagree", score: 2, icon: "diamond" },
          { value: "d", text: "Strongly Disagree", score: 1, icon: "x" },
        ],
      },
      {
        id: "c2",
        text: "I complete tasks thoroughly.",
        options: [
          { value: "a", text: "Strongly Agree", score: 4, icon: "check" },
          { value: "b", text: "Agree", score: 3, icon: "triangle" },
          { value: "c", text: "Disagree", score: 2, icon: "diamond" },
          { value: "d", text: "Strongly Disagree", score: 1, icon: "x" },
        ],
      },
      {
        id: "c3",
        text: "I plan ahead rather than act on impulse.",
        options: [
          { value: "a", text: "Strongly Agree", score: 4, icon: "check" },
          { value: "b", text: "Agree", score: 3, icon: "triangle" },
          { value: "c", text: "Disagree", score: 2, icon: "diamond" },
          { value: "d", text: "Strongly Disagree", score: 1, icon: "x" },
        ],
      },
      {
        id: "c4",
        text: "I keep my work and living space clean and organized.",
        options: [
          { value: "a", text: "Strongly Agree", score: 4, icon: "check" },
          { value: "b", text: "Agree", score: 3, icon: "triangle" },
          { value: "c", text: "Disagree", score: 2, icon: "diamond" },
          { value: "d", text: "Strongly Disagree", score: 1, icon: "x" },
        ],
      },
      {
        id: "c5",
        text: "I am dependable and responsible.",
        options: [
          { value: "a", text: "Strongly Agree", score: 4, icon: "check" },
          { value: "b", text: "Agree", score: 3, icon: "triangle" },
          { value: "c", text: "Disagree", score: 2, icon: "diamond" },
          { value: "d", text: "Strongly Disagree", score: 1, icon: "x" },
        ],
      },
    ],
  },
  extraversion: {
    title: " Extraversion",
    description: "Sociability, energy, assertiveness",
    questions: [
      {
        id: "e1",
        text: "I enjoy being the center of attention.",
        options: [
          { value: "a", text: "Strongly Agree", score: 4, icon: "check" },
          { value: "b", text: "Agree", score: 3, icon: "triangle" },
          { value: "c", text: "Disagree", score: 2, icon: "diamond" },
          { value: "d", text: "Strongly Disagree", score: 1, icon: "x" },
        ],
      },
      {
        id: "e2",
        text: "I feel energized after social interactions.",
        options: [
          { value: "a", text: "Strongly Agree", score: 4, icon: "check" },
          { value: "b", text: "Agree", score: 3, icon: "triangle" },
          { value: "c", text: "Disagree", score: 2, icon: "diamond" },
          { value: "d", text: "Strongly Disagree", score: 1, icon: "x" },
        ],
      },
      {
        id: "e3",
        text: "I start conversations easily.",
        options: [
          { value: "a", text: "Strongly Agree", score: 4, icon: "check" },
          { value: "b", text: "Agree", score: 3, icon: "triangle" },
          { value: "c", text: "Disagree", score: 2, icon: "diamond" },
          { value: "d", text: "Strongly Disagree", score: 1, icon: "x" },
        ],
      },
      {
        id: "e4",
        text: "I seek out social gatherings.",
        options: [
          { value: "a", text: "Strongly Agree", score: 4, icon: "check" },
          { value: "b", text: "Agree", score: 3, icon: "triangle" },
          { value: "c", text: "Disagree", score: 2, icon: "diamond" },
          { value: "d", text: "Strongly Disagree", score: 1, icon: "x" },
        ],
      },
      {
        id: "e5",
        text: "I prefer working with others over working alone.",
        options: [
          { value: "a", text: "Strongly Agree", score: 4, icon: "check" },
          { value: "b", text: "Agree", score: 3, icon: "triangle" },
          { value: "c", text: "Disagree", score: 2, icon: "diamond" },
          { value: "d", text: "Strongly Disagree", score: 1, icon: "x" },
        ],
      },
    ],
  },
  agreeableness: {
    title: " Agreeableness",
    description: "Trust, altruism, kindness, affection",
    questions: [
      {
        id: "a1",
        text: "I sympathize with others' feelings.",
        options: [
          { value: "a", text: "Strongly Agree", score: 4, icon: "check" },
          { value: "b", text: "Agree", score: 3, icon: "triangle" },
          { value: "c", text: "Disagree", score: 2, icon: "diamond" },
          { value: "d", text: "Strongly Disagree", score: 1, icon: "x" },
        ],
      },
      {
        id: "a2",
        text: "I go out of my way to help people.",
        options: [
          { value: "a", text: "Strongly Agree", score: 4, icon: "check" },
          { value: "b", text: "Agree", score: 3, icon: "triangle" },
          { value: "c", text: "Disagree", score: 2, icon: "diamond" },
          { value: "d", text: "Strongly Disagree", score: 1, icon: "x" },
        ],
      },
      {
        id: "a3",
        text: "I avoid arguing, even when I disagree.",
        options: [
          { value: "a", text: "Strongly Agree", score: 4, icon: "check" },
          { value: "b", text: "Agree", score: 3, icon: "triangle" },
          { value: "c", text: "Disagree", score: 2, icon: "diamond" },
          { value: "d", text: "Strongly Disagree", score: 1, icon: "x" },
        ],
      },
      {
        id: "a4",
        text: "I trust that people have good intentions.",
        options: [
          { value: "a", text: "Strongly Agree", score: 4, icon: "check" },
          { value: "b", text: "Agree", score: 3, icon: "triangle" },
          { value: "c", text: "Disagree", score: 2, icon: "diamond" },
          { value: "d", text: "Strongly Disagree", score: 1, icon: "x" },
        ],
      },
      {
        id: "a5",
        text: "I'm patient and tolerant with others.",
        options: [
          { value: "a", text: "Strongly Agree", score: 4, icon: "check" },
          { value: "b", text: "Agree", score: 3, icon: "triangle" },
          { value: "c", text: "Disagree", score: 2, icon: "diamond" },
          { value: "d", text: "Strongly Disagree", score: 1, icon: "x" },
        ],
      },
    ],
  },
  neuroticism: {
    title: " Neuroticism",
    description: "Emotional instability, anxiety, moodiness",
    questions: [
      {
        id: "n1",
        text: "I often feel anxious or worried.",
        options: [
          { value: "a", text: "Strongly Agree", score: 4, icon: "check" },
          { value: "b", text: "Agree", score: 3, icon: "triangle" },
          { value: "c", text: "Disagree", score: 2, icon: "diamond" },
          { value: "d", text: "Strongly Disagree", score: 1, icon: "x" },
        ],
      },
      {
        id: "n2",
        text: "I get upset easily.",
        options: [
          { value: "a", text: "Strongly Agree", score: 4, icon: "check" },
          { value: "b", text: "Agree", score: 3, icon: "triangle" },
          { value: "c", text: "Disagree", score: 2, icon: "diamond" },
          { value: "d", text: "Strongly Disagree", score: 1, icon: "x" },
        ],
      },
      {
        id: "n3",
        text: "I have frequent mood swings.",
        options: [
          { value: "a", text: "Strongly Agree", score: 4, icon: "check" },
          { value: "b", text: "Agree", score: 3, icon: "triangle" },
          { value: "c", text: "Disagree", score: 2, icon: "diamond" },
          { value: "d", text: "Strongly Disagree", score: 1, icon: "x" },
        ],
      },
      {
        id: "n4",
        text: "I struggle to stay calm under pressure.",
        options: [
          { value: "a", text: "Strongly Agree", score: 4, icon: "check" },
          { value: "b", text: "Agree", score: 3, icon: "triangle" },
          { value: "c", text: "Disagree", score: 2, icon: "diamond" },
          { value: "d", text: "Strongly Disagree", score: 1, icon: "x" },
        ],
      },
      {
        id: "n5",
        text: "I often feel overwhelmed by my emotions.",
        options: [
          { value: "a", text: "Strongly Agree", score: 4, icon: "check" },
          { value: "b", text: "Agree", score: 3, icon: "triangle" },
          { value: "c", text: "Disagree", score: 2, icon: "diamond" },
          { value: "d", text: "Strongly Disagree", score: 1, icon: "x" },
        ],
      },
    ],
  },
};

// Flatten quizData into a single array of questions, adding dimension info to each question
const allQuestions = Object.values(quizData).flatMap((dimension) =>
  dimension.questions.map((q) => ({
    ...q,
    dimensionTitle: dimension.title,
    dimensionDescription: dimension.description,
  }))
);
const totalQuestions = allQuestions.length;

const OptionIcon = ({ type, selected }) => {
  const baseClasses = "w-5 h-5";
  const selectedClasses = selected ? "text-white" : "";
  switch (type) {
    case "check":
      return <CheckCircle className={`${baseClasses} ${selected ? "text-white" : "text-green-600"}`} />;
    case "triangle":
      return <AlertTriangle className={`${baseClasses} ${selected ? "text-white" : "text-yellow-600"}`} />;
    case "diamond":
      return <Minus className={`${baseClasses} ${selected ? "text-white" : "text-blue-600"}`} />;
    case "x":
      return <XCircle className={`${baseClasses} ${selected ? "text-white" : "text-red-600"}`} />;
    default:
      return <CheckCircle className={baseClasses} />;
  }
};

const getDimensionIcon = (dimensionKey) => {
  switch (dimensionKey) {
    case 'openness': return <Lightbulb className="w-6 h-6" />;
    case 'conscientiousness': return <ClipboardCheck className="w-6 h-6" />;
    case 'extraversion': return <Users className="w-6 h-6" />;
    case 'agreeableness': return <Handshake className="w-6 h-6" />;
    case 'neuroticism': return <CloudLightning className="w-6 h-6" />;
    default: return <Brain className="w-6 h-6" />;
  }
};

const getDimensionColorClasses = (dimensionKey) => {
  switch (dimensionKey) {
    case 'openness': return 'text-blue-600 bg-blue-50 border-blue-200';
    case 'conscientiousness': return 'text-green-600 bg-green-50 border-green-200';
    case 'extraversion': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    case 'agreeableness': return 'text-purple-600 bg-purple-50 border-purple-200';
    case 'neuroticism': return 'text-red-600 bg-red-50 border-red-200';
    default: return 'text-gray-600 bg-gray-50 border-gray-200';
  }
};

const BigFiveTest = () => {
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState("intro");
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0); // Changed from currentDimension
  const [answers, setAnswers] = useState({});
  const [scores, setScores] = useState({});
  // const { toast } = useToast(); // Initialize useToast
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [previousResults, setPreviousResults] = useState([]);
  const [showPreviousResults, setShowPreviousResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const [aiReportLoading, setAiReportLoading] = useState(false);
  const [aiReportError, setAiReportError] = useState(null);
  const [aiReportText, setAiReportText] = useState("");
  const currentQuestion = allQuestions[currentQuestionIndex];
  const answeredQuestionsCount = Object.keys(answers).length;
  const progress = (answeredQuestionsCount / totalQuestions) * 100;
const [autoNext, setAutoNext] = useState(false);
  // Fetch previous Big Five results
  const fetchPreviousResults = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      setLoading(true);
      const { data } = await api.get("/api/test-results?testType=bigfive");
      setPreviousResults(data.testResults || []);
    } catch (error) {
      console.error("Error fetching previous results:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPreviousResults();
  }, []);

  // Check for showResults URL parameter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const showResultsParam = params.get('showResults');
    if (showResultsParam === 'true') {
      setShowPreviousResults(true);
    }
  }, [location.search]);

  const handleAnswerChange = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
     setAutoNext(true); 
  };

  const calculateScores = () => {
    const newScores = {};
    Object.entries(quizData).forEach(([dimension, data]) => {
      let totalScore = 0;
      data.questions.forEach((question) => {
        const answer = answers[question.id];
        if (answer) {
          const option = question.options.find((opt) => opt.value === answer);
          if (option) {
            totalScore += option.score;
          }
        }
      });
      newScores[dimension] = totalScore;
    });
    setScores(newScores);

    // Notify test completion for real-time updates
    const testData = {
      scores: newScores,
      answers: answers,
      overallScore: Math.round(Object.values(newScores).reduce((sum, score) => sum + score, 0) / Object.keys(newScores).length * 5), // Convert to percentage
      testType: 'bigfive',
      completed: true
    };

    notifyTestCompletion('bigfive', testData);

    setCurrentStep("results");
  };

  const getScoreLevel = (score, maxScore = 20) => {
    const percentage = (score / maxScore) * 100;
    if (percentage >= 80) return { level: "Very High", color: "bg-red-500" };
    if (percentage >= 65) return { level: "High", color: "bg-orange-500" };
    if (percentage >= 50) return { level: "Moderate", color: "bg-yellow-500" };
    if (percentage >= 35) return { level: "Low", color: "bg-blue-500" };
    return { level: "Very Low", color: "bg-green-500" };
  };

  const getPersonalityDescription = (dimensionKey, score) => {
    const dimensionData = quizData[dimensionKey];
    const { level } = getScoreLevel(score, dimensionData.questions.length * 4); // Max score is 4 per question
    const descriptions = {
      openness: {
        "Very High": "You are highly open to new experiences, creative, and imaginative.",
        "High": "You are open-minded and enjoy exploring new ideas and experiences.",
        "Moderate": "You have a balanced approach to new experiences and ideas.",
        "Low": "You prefer familiar routines and traditional approaches.",
        "Very Low": "You strongly prefer stability and conventional ways of thinking."
      },
      conscientiousness: {
        "Very High": "You are extremely organized, responsible, and goal-oriented.",
        "High": "You are reliable, organized, and pay attention to details.",
        "Moderate": "You have a balanced approach to planning and spontaneity.",
        "Low": "You prefer flexibility and may be more spontaneous.",
        "Very Low": "You strongly prefer flexibility and may avoid strict planning."
      },
      extraversion: {
        "Very High": "You are highly energetic, outgoing, and enjoy social interactions.",
        "High": "You are sociable, energetic, and enjoy being around others.",
        "Moderate": "You have a balanced approach to social interactions.",
        "Low": "You prefer quiet time and may be more reserved.",
        "Very Low": "You strongly prefer solitude and quiet environments."
      },
      agreeableness: {
        "Very High": "You are extremely cooperative, trusting, and compassionate.",
        "High": "You are friendly, cooperative, and considerate of others.",
        "Moderate": "You have a balanced approach to cooperation and assertiveness.",
        "Low": "You may be more direct and competitive in interactions.",
        "Very Low": "You strongly prefer directness and may be more challenging."
      },
      neuroticism: {
        "Very High": "You may experience high levels of stress and emotional sensitivity.",
        "High": "You may be sensitive to stress and experience mood fluctuations.",
        "Moderate": "You have a balanced emotional response to stress.",
        "Low": "You are generally calm and emotionally stable.",
        "Very Low": "You are extremely calm and emotionally resilient."
      }
    };
    return descriptions[dimensionKey]?.[level] || "No description available.";
  };

  const nextQuestion = () => {
    if (!answers[currentQuestion.id]) {
      // toast({
      //   title: "Please answer the current question",
      //   description: "You must select an option before proceeding.",
      //   variant: "destructive",
      // });
      return;
    }
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      calculateScores();
    }
  };

  const prevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const resetQuiz = () => {
    setCurrentStep("intro");
    setCurrentQuestionIndex(0);
    setAnswers({});
    setScores({});
  };

  const buildDetailedAnswers = () => {
    const detailedAnswers = [];
    Object.entries(quizData).forEach(([dimension, data]) => {
      data.questions.forEach((question) => {
        const selectedValue = answers[question.id];
        if (selectedValue) {
          const selectedOption = question.options.find((opt) => opt.value === selectedValue);
          if (selectedOption) {
            detailedAnswers.push({
              questionId: question.id,
              questionText: question.text,
              selectedOptionId: selectedOption.value,
              selectedOptionText: selectedOption.text
            });
          }
        }
      });
    });
    return detailedAnswers;
  };

  const fetchAIReport = async () => {
    try {
      setAiReportLoading(true);
      setAiReportError(null);
      const detailedAnswers = buildDetailedAnswers();
      const overallScore = Object.values(scores).reduce((sum, s) => sum + s, 0);
      const overallPercentage = (overallScore / (Object.keys(scores).length * 20)) * 100;
      const payload = {
        testType: 'bigfive',
        scores,
        answers: detailedAnswers,
        overallScore,
        overallPercentage,
      };
      const { report } = await generateAIReport(payload);
      setAiReportText(report);
    } catch (err) {
      setAiReportError(err?.response?.data?.message || 'Failed to generate AI report');
    } finally {
      setAiReportLoading(false);
    }
  };

  const saveTestResult = async (scoresToSave) => {
    try {
      const detailedAnswers = buildDetailedAnswers();

      const overallScore = Object.values(scoresToSave).reduce(
        (sum, score) => sum + score,
        0
      );
      const overallPercentage =
        (overallScore / (Object.keys(scoresToSave).length * 20)) * 100;

      await api.post("/api/test-results", {
        testType: "bigfive",
        answers: detailedAnswers,
        scores: scoresToSave,
        overallScore,
        overallPercentage,
      });

      toast.success("Test results saved successfully!");
    } catch (error) {
      console.error("Error saving test result:", error);
      toast.error(
        error.response?.data?.message || "Failed to save test results"
      );
    }
  };

  // Save results when they are first shown
  useEffect(() => {
    if (currentStep === "results" && Object.keys(scores).length > 0) {
      saveTestResult(scores);
    }
  }, [currentStep, scores]);

  useEffect(() => {
    if (currentStep === "report" && !aiReportText && !aiReportLoading) {
      fetchAIReport();
    }
  }, [currentStep]);
useEffect(() => {
  if (autoNext) {
    nextQuestion();
    setAutoNext(false);
  }
}, [autoNext]);
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };
  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const getDetailedReport = () => {
    const getScoreLevel = (score, maxScore = 20) => {
      const percentage = (score / maxScore) * 100;
      if (percentage >= 80) return { level: "Very High", color: "bg-red-500" };
      if (percentage >= 65) return { level: "High", color: "bg-orange-500" };
      if (percentage >= 50) return { level: "Moderate", color: "bg-yellow-500" };
      if (percentage >= 35) return { level: "Low", color: "bg-blue-500" };
      return { level: "Very Low", color: "bg-green-500" };
    };

    const getPersonalityDescription = (dimensionKey, score) => {
      const dimensionData = quizData[dimensionKey];
      const { level } = getScoreLevel(score, dimensionData.questions.length * 4);
      const descriptions = {
        openness: {
          "Very High": "You are highly open to new experiences, creative, and imaginative.",
          "High": "You are open-minded and enjoy exploring new ideas and experiences.",
          "Moderate": "You have a balanced approach to new experiences and ideas.",
          "Low": "You prefer familiar routines and traditional approaches.",
          "Very Low": "You strongly prefer stability and conventional ways of thinking."
        },
        conscientiousness: {
          "Very High": "You are extremely organized, responsible, and goal-oriented.",
          "High": "You are reliable, organized, and pay attention to details.",
          "Moderate": "You have a balanced approach to planning and spontaneity.",
          "Low": "You prefer flexibility and may be more spontaneous.",
          "Very Low": "You strongly prefer flexibility and may avoid strict planning."
        },
        extraversion: {
          "Very High": "You are highly energetic, outgoing, and enjoy social interactions.",
          "High": "You are sociable, energetic, and enjoy being around others.",
          "Moderate": "You have a balanced approach to social interactions.",
          "Low": "You prefer quiet time and may be more reserved.",
          "Very Low": "You strongly prefer solitude and quiet environments."
        },
        agreeableness: {
          "Very High": "You are extremely cooperative, trusting, and compassionate.",
          "High": "You are friendly, cooperative, and considerate of others.",
          "Moderate": "You have a balanced approach to cooperation and assertiveness.",
          "Low": "You may be more direct and competitive in interactions.",
          "Very Low": "You strongly prefer directness and may be more challenging."
        },
        neuroticism: {
          "Very High": "You may experience high levels of stress and emotional sensitivity.",
          "High": "You may be sensitive to stress and experience mood fluctuations.",
          "Moderate": "You have a balanced emotional response to stress.",
          "Low": "You are generally calm and emotionally stable.",
          "Very Low": "You are extremely calm and emotionally resilient."
        }
      };
      return descriptions[dimensionKey]?.[level] || "No description available.";
    };

    // Comprehensive Big Five insights
    const bigFiveInsights = {
      openness: {
        title: "Openness to Experience",
        icon: "🌟",
        description: "Your willingness to try new things and embrace change",
        highTraits: ["Creative", "Imaginative", "Curious", "Adventurous", "Artistic", "Philosophical"],
        lowTraits: ["Practical", "Traditional", "Conventional", "Down-to-earth", "Conservative", "Stable"],
        careerPaths: {
          "Very High": ["Artist", "Writer", "Scientist", "Entrepreneur", "Travel Guide", "Innovation Manager"],
          "High": ["Designer", "Researcher", "Teacher", "Marketing", "Consultant", "Creative Director"],
          "Moderate": ["Manager", "Analyst", "Coordinator", "Specialist", "Team Lead", "Project Manager"],
          "Low": ["Accountant", "Administrator", "Technician", "Operator", "Supervisor", "Quality Control"],
          "Very Low": ["Data Entry", "Assembly Worker", "Security Guard", "Maintenance", "Clerk", "Machine Operator"]
        },
        relationships: {
          "Very High": "You seek partners who share your curiosity and love for adventure. You value intellectual stimulation and creative expression in relationships.",
          "High": "You enjoy relationships that offer growth and new experiences. You appreciate partners who are open-minded and willing to explore.",
          "Moderate": "You balance stability with novelty in relationships. You appreciate both routine and occasional surprises.",
          "Low": "You value stability and reliability in relationships. You prefer partners who are consistent and predictable.",
          "Very Low": "You strongly prefer stable, traditional relationships. You value consistency and avoid unnecessary changes."
        },
        growthAreas: {
          "Very High": "Focus on follow-through and practical implementation of your creative ideas.",
          "High": "Balance your openness with developing practical skills and consistency.",
          "Moderate": "You're well-balanced. Consider developing either more creativity or more structure based on your goals.",
          "Low": "Try stepping outside your comfort zone occasionally to discover new interests.",
          "Very Low": "Consider exploring one new activity or idea to expand your horizons gradually."
        }
      },
      conscientiousness: {
        title: "Conscientiousness",
        icon: "📋",
        description: "Your level of organization, responsibility, and self-discipline",
        highTraits: ["Organized", "Reliable", "Hardworking", "Punctual", "Detail-oriented", "Goal-focused"],
        lowTraits: ["Spontaneous", "Flexible", "Easygoing", "Adaptable", "Creative", "Free-spirited"],
        careerPaths: {
          "Very High": ["Project Manager", "Accountant", "Quality Manager", "Military Officer", "Surgeon", "Air Traffic Controller"],
          "High": ["Engineer", "Teacher", "Nurse", "Administrator", "Financial Analyst", "HR Manager"],
          "Moderate": ["Sales Representative", "Customer Service", "Coordinator", "Specialist", "Team Member", "Consultant"],
          "Low": ["Artist", "Entrepreneur", "Creative Director", "Travel Guide", "Event Planner", "Freelancer"],
          "Very Low": ["Performer", "Adventure Guide", "Innovation Consultant", "Creative Writer", "Musician", "Photographer"]
        },
        relationships: {
          "Very High": "You are extremely reliable and committed in relationships. You may need to learn to be more flexible and spontaneous.",
          "High": "You are dependable and organized in relationships. You value structure and may need to practice flexibility.",
          "Moderate": "You balance responsibility with spontaneity in relationships. You're adaptable to different situations.",
          "Low": "You bring spontaneity and creativity to relationships. You may need to work on reliability and planning.",
          "Very Low": "You are very flexible and spontaneous in relationships. You may need to develop more consistency and follow-through."
        },
        growthAreas: {
          "Very High": "Learn to relax and be more spontaneous. Practice flexibility and letting go of perfectionism.",
          "High": "Balance your organization with flexibility. Allow room for spontaneity and creativity.",
          "Moderate": "You're well-balanced. Consider developing either more structure or more flexibility based on your needs.",
          "Low": "Develop basic organizational skills and reliability to achieve your goals more effectively.",
          "Very Low": "Focus on developing basic planning and organizational skills to turn your creative ideas into reality."
        }
      },
      extraversion: {
        title: "Extraversion",
        icon: "🎉",
        description: "Your energy level and preference for social interaction",
        highTraits: ["Energetic", "Sociable", "Talkative", "Assertive", "Enthusiastic", "Outgoing"],
        lowTraits: ["Quiet", "Reserved", "Thoughtful", "Independent", "Calm", "Reflective"],
        careerPaths: {
          "Very High": ["Sales Manager", "Event Planner", "Public Relations", "Teacher", "Entertainer", "Tour Guide"],
          "High": ["Marketing", "Customer Service", "HR Manager", "Trainer", "Recruiter", "Team Leader"],
          "Moderate": ["Consultant", "Coordinator", "Specialist", "Manager", "Analyst", "Project Lead"],
          "Low": ["Researcher", "Writer", "Programmer", "Accountant", "Librarian", "Data Analyst"],
          "Very Low": ["Scientist", "Author", "Software Developer", "Archivist", "Researcher", "Independent Consultant"]
        },
        relationships: {
          "Very High": "You are highly social and energetic in relationships. You may need to learn to listen and give others space.",
          "High": "You are sociable and engaging in relationships. You bring energy and enthusiasm to your connections.",
          "Moderate": "You balance social interaction with quiet time in relationships. You're adaptable to different social situations.",
          "Low": "You prefer deep, meaningful connections over large social groups. You may need to practice initiating social interactions.",
          "Very Low": "You value deep, intimate relationships and prefer quality over quantity in social connections."
        },
        growthAreas: {
          "Very High": "Practice active listening and giving others space to express themselves. Learn to enjoy quiet moments.",
          "High": "Balance your social energy with quiet reflection. Practice listening and being present for others.",
          "Moderate": "You're well-balanced. Consider developing either more social skills or more introspection based on your goals.",
          "Low": "Practice initiating social interactions and stepping outside your comfort zone gradually.",
          "Very Low": "Consider joining small groups or activities that align with your interests to build social confidence."
        }
      },
      agreeableness: {
        title: "Agreeableness",
        icon: "🤝",
        description: "Your tendency to be cooperative and trusting toward others",
        highTraits: ["Trusting", "Cooperative", "Compassionate", "Helpful", "Modest", "Sympathetic"],
        lowTraits: ["Direct", "Competitive", "Challenging", "Assertive", "Independent", "Decisive"],
        careerPaths: {
          "Very High": ["Counselor", "Social Worker", "Nurse", "Teacher", "Customer Service", "Non-profit Worker"],
          "High": ["HR Manager", "Coordinator", "Team Leader", "Sales Representative", "Trainer", "Consultant"],
          "Moderate": ["Manager", "Analyst", "Specialist", "Coordinator", "Team Member", "Project Lead"],
          "Low": ["Lawyer", "Entrepreneur", "Sales Manager", "Executive", "Consultant", "Business Owner"],
          "Very Low": ["Prosecutor", "Debt Collector", "Security Manager", "Compliance Officer", "Auditor", "Risk Manager"]
        },
        relationships: {
          "Very High": "You are extremely caring and supportive in relationships. You may need to learn to set boundaries and express your needs.",
          "High": "You are cooperative and considerate in relationships. You value harmony and may need to practice assertiveness.",
          "Moderate": "You balance cooperation with assertiveness in relationships. You're good at finding win-win solutions.",
          "Low": "You are direct and honest in relationships. You may need to practice empathy and consider others' feelings.",
          "Very Low": "You are very direct and challenging in relationships. You may need to develop more empathy and cooperation."
        },
        growthAreas: {
          "Very High": "Learn to set healthy boundaries and express your own needs. Practice saying 'no' when necessary.",
          "High": "Balance your cooperation with healthy assertiveness. Practice expressing your opinions and needs.",
          "Moderate": "You're well-balanced. Consider developing either more empathy or more assertiveness based on your goals.",
          "Low": "Practice empathy and consider others' perspectives. Learn to balance directness with kindness.",
          "Very Low": "Focus on developing empathy and understanding others' feelings. Practice active listening and cooperation."
        }
      },
      neuroticism: {
        title: "Neuroticism",
        icon: "😌",
        description: "Your tendency to experience negative emotions and stress",
        highTraits: ["Sensitive", "Emotional", "Worried", "Anxious", "Moody", "Self-critical"],
        lowTraits: ["Calm", "Stable", "Relaxed", "Confident", "Optimistic", "Resilient"],
        careerPaths: {
          "Very High": ["Counselor", "Artist", "Writer", "Therapist", "Social Worker", "Creative Professional"],
          "High": ["Teacher", "Nurse", "Customer Service", "Coordinator", "Specialist", "Support Worker"],
          "Moderate": ["Manager", "Analyst", "Coordinator", "Team Member", "Specialist", "Project Lead"],
          "Low": ["Executive", "Entrepreneur", "Sales Manager", "Consultant", "Business Owner", "Leader"],
          "Very Low": ["CEO", "Entrepreneur", "Executive", "High-stakes Professional", "Crisis Manager", "Leader"]
        },
        relationships: {
          "Very High": "You are very sensitive and empathetic in relationships. You may need to develop emotional resilience and self-care.",
          "High": "You are caring and understanding in relationships. You may need to practice emotional regulation and stress management.",
          "Moderate": "You have balanced emotional responses in relationships. You're generally stable and supportive.",
          "Low": "You are calm and stable in relationships. You provide emotional security and may need to develop more empathy.",
          "Very Low": "You are extremely calm and resilient in relationships. You may need to develop more emotional sensitivity and empathy."
        },
        growthAreas: {
          "Very High": "Focus on developing emotional resilience, stress management, and self-care practices.",
          "High": "Practice emotional regulation techniques and stress management. Develop healthy coping mechanisms.",
          "Moderate": "You're well-balanced. Consider developing either more emotional resilience or more empathy based on your needs.",
          "Low": "You're naturally resilient. Consider developing more emotional sensitivity and empathy for others.",
          "Very Low": "You're naturally very stable. Consider developing more emotional awareness and empathy for others' feelings."
        }
      }
    };

    

    return (
      <div className="space-y-6">
        {/* Big Five Overview */}
        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <CardHeader>
            <CardTitle className="text-2xl font-bold text-blue-800 flex items-center gap-3">
              🧠 Big Five Personality Profile
            </CardTitle>
            <CardDescription className="text-blue-700 text-lg">
              Your comprehensive personality analysis across the five core dimensions
            </CardDescription>
          </CardHeader>
        </Card>

        {/* Detailed Analysis for Each Dimension */}
        {Object.entries(scores).map(([dimension, score]) => {
          const insight = bigFiveInsights[dimension];
          const { level, color } = getScoreLevel(score, quizData[dimension].questions.length * 4);
          const maxScore = quizData[dimension].questions.length * 4;
          const percentage = Math.round((score / maxScore) * 100);

          return (
            <Card key={dimension} className="bg-white border-gray-200">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="text-3xl">{insight.icon}</div>
                    <div>
                      <CardTitle className="text-xl font-semibold text-gray-800">
                        {insight.title}
                      </CardTitle>
                      <CardDescription className="text-gray-600">
                        {insight.description}
                      </CardDescription>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-gray-800">{score}/{maxScore}</div>
                    <span className={`px-3 py-1 rounded-full text-sm text-white ${color}`}>
                      {level}
                    </span>
                  </div>
                </div>
                <Progress value={percentage} className="h-2" />
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Personality Description */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-800 mb-2">Your Profile</h4>
                  <p className="text-gray-700">{getPersonalityDescription(dimension, score)}</p>
                </div>

                {/* Key Traits */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-semibold text-green-700 mb-2">Key Strengths</h4>
                    <div className="flex flex-wrap gap-2">
                      {insight.highTraits.map((trait, index) => (
                        <span key={index} className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                          {trait}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-700 mb-2">Complementary Traits</h4>
                    <div className="flex flex-wrap gap-2">
                      {insight.lowTraits.map((trait, index) => (
                        <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                          {trait}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Career Guidance */}
                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-800 mb-2">Career Recommendations</h4>
                  <div className="flex flex-wrap gap-2">
                    {insight.careerPaths[level].map((career, index) => (
                      <span key={index} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                        {career}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Relationships & Growth */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="bg-pink-50 rounded-lg p-4">
                    <h4 className="font-semibold text-pink-800 mb-2">Relationships</h4>
                    <p className="text-pink-700 text-sm leading-relaxed">{insight.relationships[level]}</p>
                  </div>
                  <div className="bg-emerald-50 rounded-lg p-4">
                    <h4 className="font-semibold text-emerald-800 mb-2">Personal Growth</h4>
                    <p className="text-emerald-700 text-sm leading-relaxed">{insight.growthAreas[level]}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}

        {/* Overall Personality Summary */}
        <Card className="bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
          <CardHeader>
            <CardTitle className="text-xl font-semibold text-purple-800 flex items-center gap-2">
              🎯 Overall Personality Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">
                  {Math.round(Object.values(scores).reduce((sum, score) => sum + score, 0) / Object.keys(scores).length)}
                </div>
                <div className="text-sm text-gray-600">Average Score</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {Object.values(scores).filter(score => score >= 16).length}
                </div>
                <div className="text-sm text-gray-600">High Scores</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {Object.values(scores).filter(score => score <= 8).length}
                </div>
                <div className="text-sm text-gray-600">Low Scores</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <div className="flex flex-col min-h-screen w-full overflow-x-hidden" style={{ background: 'linear-gradient(90deg, #FFFFFF 0%, #EFF6FF 100%, #F9FAFB 100%)' }}>
      {/* Sidebar */}
      <div className="fixed top-0 left-0 h-screen w-64 bg-white shadow-md z-10">
        <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} />
      </div>

      <div className="ml-0 md:ml-64 min-h-screen">
        <Navbar />

        {/* Show previous results view */}
        {showPreviousResults && (
          <div className="min-h-screen p-4">
            <div className="max-w-4xl mx-auto">
              <Card className="bg-[#ffffff]">
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <div>
                      <CardTitle className="text-2xl font-bold">Previous Big Five Results</CardTitle>
                      <CardDescription>Your past Big Five personality assessments</CardDescription>
                    </div>
                    <Button
                      onClick={() => setShowPreviousResults(false)}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      Take New Test
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
                      <p className="mt-4 text-gray-600">Loading results...</p>
                    </div>
                  ) : previousResults.length > 0 ? (
                    <div className="space-y-4">
                      {previousResults.map((result, index) => (
                        <Card key={result._id || index} className="border border-gray-200 hover:shadow-md transition-shadow">
                          <CardContent className="p-4">
                            <div className="flex justify-between items-start">
                              <div>
                                <h3 className="font-semibold text-gray-800">
                                  Test #{previousResults.length - index} - {new Date(result.completedAt).toLocaleDateString()}
                                </h3>
                                <p className="text-sm text-gray-600 mt-1">
                                  Overall Score: {result.overallPercentage || result.overallScore || 'N/A'}%
                                </p>
                              </div>
                              <Button
                                variant="outline"
                                onClick={() => {
                                  setScores(result.scores || {});
                                  setCurrentStep("results");
                                  setShowPreviousResults(false);
                                }}
                                className="text-purple-600 border-purple-600 hover:bg-purple-50"
                              >
                                View Results
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-gray-500">No previous results found</p>
                      <p className="text-gray-400 text-sm mt-1">Complete your first Big Five test to see results here</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {!showPreviousResults && (
          <>
            {currentStep === "intro" && (
              <div className="min-h-screen  p-4">
                <div className="max-w-2xl mx-auto">
                  <Card className="text-center bg-[#ffffff]">
                    <CardHeader className="space-y-4">
                      {/* <div className="mx-auto w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center">
                    <Brain className="w-8 h-8 text-purple-600" />
                  </div> */}
                      <CardTitle className="text-3xl font-bold ">Big Five Personality Test</CardTitle>
                      <CardDescription className="text-lg text-gray-600">
                        Detailed Analysis & Development Recommendations
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                        <div className="space-y-2">
                          <h3 className="font-semibold text-blue-600"> Openness</h3>
                          <p className="text-sm text-gray-600">Creativity and curiosity</p>
                        </div>
                        <div className="space-y-2">
                          <h3 className="font-semibold text-green-600"> Conscientiousness</h3>
                          <p className="text-sm text-gray-600">Organization and discipline</p>
                        </div>
                        <div className="space-y-2">
                          <h3 className="font-semibold text-yellow-600"> Extraversion</h3>
                          <p className="text-sm text-gray-600">Social energy and assertiveness</p>
                        </div>
                        <div className="space-y-2">
                          <h3 className="font-semibold text-purple-600"> Agreeableness</h3>
                          <p className="text-sm text-gray-600">Cooperation and trust</p>
                        </div>
                        <div className="space-y-2 md:col-span-2">
                          <h3 className="font-semibold text-red-600"> Neuroticism</h3>
                          <p className="text-sm text-gray-600">Emotional stability and stress response</p>
                        </div>
                      </div>
                      <div className="bg-purple-50 p-4 rounded-lg">
                        <p className="text-sm text-purple-800">
                          <strong>{totalQuestions} questions</strong>  • Get detailed insights into your
                          personality
                        </p>
                      </div>
                      <Button
                        onClick={() => setCurrentStep("quiz")}
                        className="w-full text-lg py-6 bg-purple-600 hover:bg-purple-700"
                        size="lg"
                      >
                        Start Test <ArrowRight className="ml-2 w-5 h-5" />
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}

            {currentStep === "quiz" && (
              <div className="min-h-screen  p-4">
                <div className=" mx-auto" style={{ width: '85%' }}>
                  {/* Header */}
                  <div className="text-center mb-8">
                    {/* <div className="mx-auto w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center mb-4">
                        <Brain className="w-6 h-6 text-white" />
                      </div> */}
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">Big Five Personality Test</h1>
                    <p className="text-gray-600">Detailed Analysis & Development Recommendations</p>
                  </div>

                  <Card className="mb-6 bg-[#ffffff]">
                    <CardHeader className="pb-4">
                      <h3 className="font-semibold mb-3">Your Progress by Category</h3>
                    </CardHeader>
                    <CardContent className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                      {Object.entries(quizData).map(([dimensionKey, data]) => {
                        let answeredCount = 0;
                        data.questions.forEach((question) => {
                          if (answers[question.id]) {
                            answeredCount++;
                          }
                        });
                        const totalQuestionsInDimension = data.questions.length;
                        const colorClasses = getDimensionColorClasses(dimensionKey);

                        return (
                          <div key={dimensionKey} className="flex flex-col items-center text-center p-2">
                            <div className={`w-10 h-10 p-2 rounded-full w-fit mx-auto mb-1 ${colorClasses}`}>
                              {getDimensionIcon(dimensionKey)}
                            </div>
                            <h4 className="font-semibold text-sm text-gray-800">{data.title.split(" ")[1]}</h4>
                            <p className="text-xs text-gray-500">{answeredCount}/{totalQuestionsInDimension}</p>
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>
                  {/* Progress Section */}
                  <Card className="mb-6  shadow-none border-none">
                    <CardContent className="p-0">
                      <div className="flex items-center justify-between mb-2 px-2">
                        <span className="text-sm text-gray-800 font-medium">
                          Question {currentQuestionIndex + 1} of {totalQuestions}
                        </span>
                        <span className="text-sm text-gray-800 font-medium">
                          {Math.round(progress)}% Complete
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-300"
                          style={{ width: `${progress}%`, backgroundColor: '#8B5CF6' }}
                        />
                      </div>
                    </CardContent>
                  </Card>


                  <Card className="bg-[#ffffff]">
                    <CardHeader className="border-b">
                      <div className="flex items-center space-x-3">
                        {/* <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                        {currentQuestionIndex + 1}
                      </div>
                    </div> */}
                        <div>
                          <CardTitle className="text-xl">{currentQuestion.dimensionTitle}</CardTitle>
                          <CardDescription>{currentQuestion.dimensionDescription}</CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="p-6">
                      <div className="space-y-8">
                        <div key={currentQuestion.id} className="space-y-4">
                          <div className="flex items-start justify-between">
                            <h3 className="font-medium text-lg text-gray-900 flex-1">
                              {currentQuestionIndex + 1}. {currentQuestion.text}
                            </h3>
                            {/* {!answers[currentQuestion.id] && (
                              <Badge variant="destructive" className="ml-4">
                                Unanswered
                              </Badge>
                            )} */}
                          </div>
                          <div className="space-y-3">
                            {currentQuestion.options.map((option) => {
                              const isSelected = answers[currentQuestion.id] === option.value;
                              return (
                                <button
                                  key={option.value}
                                  onClick={() => handleAnswerChange(currentQuestion.id, option.value)}
                                  className={`w-full p-2 px-4 rounded-lg border-2 transition-all duration-200 flex items-center space-x-3 text-left ${isSelected
                                    ? "border-primary ring-2 ring-primary/50 bg-primary/5 dark:bg-primary/10"
                                    : "hover:bg-[rgb(197,199,203)] dark:hover:bg-gray-800"
                                    }`}
                                >
                                  <span className="">{option.text}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </CardContent>

                    {/* Navigation Footer */}
                  </Card>
                  <div className="flex justify-between items-center p-6 pt-0 mt-8">
                    <Button
                      variant="outline"
                      onClick={prevQuestion}
                      disabled={currentQuestionIndex === 0}
                      className="bg-[#ffffff] px-4 py-2 rounded-md text-sm font-medium transition-colors focus:outline-none border border-gray-300 text-gray-700 bg-white hover:bg-gray-100 "
                    >
                      Previous
                    </Button>
                    {/* <Button
                      onClick={nextQuestion}
                      className=" bg-purple-600 hover:bg-purple-700 text-white flex items-center space-x-2"
                    >
                      <span>{currentQuestionIndex === totalQuestions - 1 ? "View Results" : "Next Question"}</span>
                      <ArrowRight className="w-4 h-4" />
                    </Button> */}
                  </div>
                </div>
              </div>
            )}

            {currentStep === "results" && (
              <div className="min-h-screen p-4">
                <div className="mx-auto" style={{ width: '85%' }}>
                  <Card className='bg-[#ffffff]'>
                    <CardHeader className="text-center">
                      <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                        <BarChart3 className="w-8 h-8 text-green-600" />
                      </div>
                      <CardTitle className="text-3xl font-bold">Your Personality Results</CardTitle>
                      <CardDescription>Based on the Big Five (OCEAN) personality model</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {Object.entries(scores).map(([dimensionKey, score]) => {
                        const dimensionData = quizData[dimensionKey];
                        const { level, color } = getScoreLevel(score, dimensionData.questions.length * 4); // Max score is 4 per question
                        const percentage = (score / (dimensionData.questions.length * 4)) * 100;
                        return (
                          <div key={dimensionKey} className="space-y-3">
                            <div className="flex justify-between items-center">
                              <h3 className="font-semibold text-lg">{dimensionData.title}</h3>
                              <div className="text-right">
                                <span className="font-bold">{score}/{dimensionData.questions.length * 4}</span>
                                <span className={`ml-2 px-2 py-1 rounded text-xs text-white ${color}`}>{level}</span>
                              </div>
                            </div>
                            <Progress value={percentage} className="h-3" />
                            <p className="text-sm text-gray-600">{dimensionData.description}</p>
                          </div>
                        );
                      })}
                      <div className="flex gap-4 pt-6">
                        <Button onClick={() => setCurrentStep("report")} className="flex-1 bg-purple-600 hover:bg-purple-700">
                          <FileText className="mr-2 w-4 h-4" />
                          View Detailed Report
                        </Button>
                        <Button variant="outline" onClick={resetQuiz}>
                          <RotateCcw className="mr-2 w-4 h-4" />
                          Retake Test
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}

            {currentStep === "report" && (
              <div className="min-h-screen p-4">
                <div className=" mx-auto space-y-6 " style={{ width: '85%' }}>
                  <Card className='bg-[#ffffff]'>
                    <CardHeader>
                      <CardTitle className="text-2xl font-bold flex items-center">
                        <FileText className="mr-3 w-6 h-6" />
                        Detailed Personality Report
                      </CardTitle>
                      <CardDescription>Comprehensive analysis of your Big Five personality traits</CardDescription>
                    </CardHeader>
                  </Card>
                  <Card className='bg-[#ffffff]'>
                    <CardHeader>
                      <CardTitle className="text-xl font-semibold">We are collecting your results</CardTitle>
                      <CardDescription>Personalized insights</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {aiReportLoading && (
                        <div className="flex items-center text-gray-600">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-2"></div>
                          Generating your report...
                        </div>
                      )}
                      {aiReportError && (
                        <div className="text-red-600 text-sm mb-2">{aiReportError}</div>
                      )}
                      {!aiReportLoading && aiReportText && (
                        <div className="prose max-w-none whitespace-pre-wrap text-gray-800 text-sm">{aiReportText}</div>
                      )}
                      {!aiReportLoading && !aiReportText && (
                        <Button onClick={fetchAIReport} className="bg-blue-600 hover:bg-blue-700 text-white">Generate Report</Button>
                      )}
                    </CardContent>
                  </Card>
                  {getDetailedReport()}
                  <Card className='bg-[#ffffff]'>
                    <CardHeader>
                      <CardTitle>Understanding Your Results</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div className="space-y-2">
                          <h4 className="font-semibold text-green-600">Very Low (0-35%)</h4>
                          <p className="text-gray-600">Significantly below average for this trait</p>
                        </div>
                        <div className="space-y-2">
                          <h4 className="font-semibold text-blue-600">Low (35-50%)</h4>
                          <p className="text-gray-600">Below average for this trait</p>
                        </div>
                        <div className="space-y-2">
                          <h4 className="font-semibold text-yellow-600">Moderate (50-65%)</h4>
                          <p className="text-gray-600">Average range for this trait</p>
                        </div>
                        <div className="space-y-2">
                          <h4 className="font-semibold text-orange-600">High (65-80%)</h4>
                          <p className="text-gray-600">Above average for this trait</p>
                        </div>
                        <div className="space-y-2 md:col-span-2">
                          <h4 className="font-semibold text-red-600">Very High (80-100%)</h4>
                          <p className="text-gray-600">Significantly above average for this trait</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <div className="flex gap-4">
                    <Button onClick={() => setCurrentStep("results")} variant="outline" className="flex-1 bg-[#ffffff]">
                      <ArrowLeft className="mr-2 w-4 h-4" />
                      Back to Results
                    </Button>
                    <Button onClick={resetQuiz} className="flex-1 bg-purple-600 hover:bg-purple-700">
                      <RotateCcw className="mr-2 w-4 h-4" />
                      Take Test Again
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );

};

export default BigFiveTest; 